import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import UserLayout from '../../components/Layout/UserLayout';
import { Card, StatCard, Button, Select, Spinner, EmptyState, PageHeader } from '../../components/UI';
import { reportsAPI } from '../../services/api';
import { formatLabel } from '../../utils/formatLabel';
import toast from 'react-hot-toast';
import { format, parseISO } from 'date-fns';
import { UtilisationBar, fmtMins } from '../../components/Reports/VisitProductivity';
import {
  HiOutlinePhone,
  HiOutlineUsers,
  HiOutlineClock,
  HiOutlineCheckCircle,
  HiOutlineRefresh,
  HiOutlineChartBar,
} from 'react-icons/hi';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const RANGE_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'quarter', label: 'This Quarter' },
];

const STATUS_COLORS = {
  connected: '#10b981',
  not_connected: '#f59e0b',
  no_answer: '#6366f1',
  busy: '#ef4444',
  rejected: '#dc2626',
  missed: '#8b5cf6',
  failed: '#64748b',
  unknown: '#94a3b8',
};

const formatDuration = (seconds = 0) => {
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
};

const formatMinutes = (minutes = 0) => {
  const m = Math.round(minutes);
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`;
};

const SummaryRow = ({ label, value }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-secondary-500">{label}</span>
    <span className="font-semibold text-secondary-900">{value}</span>
  </div>
);

const UserReports = () => {
  const [range, setRange] = useState('week');

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['myReport', range],
    queryFn: async () => {
      const response = await reportsAPI.getMyReport({ range });
      return response.data?.data || null;
    },
  });

  const handleRefresh = async () => {
    await refetch();
    toast.success('Report refreshed');
  };

  const calls = data?.calls;
  const leads = data?.leads;
  const followUps = data?.followUps;
  const attendance = data?.attendance;
  const visits = data?.visits;
  const productivity = data?.productivity;

  const trend = (data?.dailyTrend || []).map((d) => ({
    ...d,
    label: format(parseISO(d.date), 'MMM d'),
    notConnected: d.total - d.connected,
  }));
  const statusData = (calls?.byStatus || []).map((s) => ({
    name: formatLabel(s.status),
    value: s.count,
    color: STATUS_COLORS[s.status] || STATUS_COLORS.unknown,
  }));
  const maxStageCount = Math.max(1, ...(leads?.byStage || []).map((s) => s.count));
  const periodLabel = RANGE_OPTIONS.find((r) => r.value === range)?.label;

  return (
    <UserLayout>
      <div className="p-4 lg:p-6 space-y-4 sm:space-y-6">
        <PageHeader
          title="My Reports"
          description="Your calling, lead and follow-up performance."
          action={
            <>
              <Select
                options={RANGE_OPTIONS}
                value={range}
                onChange={(e) => setRange(e.target.value)}
                className="w-40"
              />
              <Button
                variant="outline"
                size="sm"
                icon={<HiOutlineRefresh className="w-4 h-4" />}
                onClick={handleRefresh}
                loading={isFetching}
              >
                Refresh
              </Button>
            </>
          }
        />

        {isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : !data ? (
          <Card>
            <EmptyState
              icon={<HiOutlineChartBar className="w-8 h-8 text-secondary-400" />}
              title="Couldn't load your report"
              description="Please refresh and try again"
            />
          </Card>
        ) : (
          <>
            {/* KPI cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
              <StatCard
                title="Total Calls"
                value={calls.total}
                icon={<HiOutlinePhone className="w-6 h-6 text-white" />}
                color="primary"
              />
              <StatCard
                title="Connect Rate"
                value={`${calls.connectRate}%`}
                icon={<HiOutlineCheckCircle className="w-6 h-6 text-white" />}
                color="success"
              />
              <StatCard
                title="Talk Time"
                value={formatDuration(calls.talkTime)}
                icon={<HiOutlineClock className="w-6 h-6 text-white" />}
                color="info"
              />
              <StatCard
                title="Leads Converted"
                value={leads.converted}
                icon={<HiOutlineUsers className="w-6 h-6 text-white" />}
                color="warning"
              />
            </div>

            {/* Call trend + outcomes */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 lg:gap-6">
              <Card padding="p-4 sm:p-6" className="xl:col-span-2">
                <h3 className="font-semibold text-secondary-900 mb-4">
                  Daily Calls <span className="text-secondary-400 font-normal">· {periodLabel}</span>
                </h3>
                {trend.length > 0 ? (
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={trend} margin={{ top: 5, right: 8, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                        <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                        <Tooltip />
                        <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                        <Bar dataKey="connected" name="Connected" stackId="a" fill="#10b981" />
                        <Bar dataKey="notConnected" name="Not Connected" stackId="a" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <EmptyState
                    icon={<HiOutlinePhone className="w-8 h-8 text-secondary-400" />}
                    title="No calls in this period"
                    description="Calls made from the TeleValley app will be reported here"
                  />
                )}
              </Card>

              <Card padding="p-4 sm:p-6">
                <h3 className="font-semibold text-secondary-900 mb-4">Call Outcomes</h3>
                {statusData.length > 0 ? (
                  <>
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2}>
                            {statusData.map((entry) => (
                              <Cell key={entry.name} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <ul className="mt-3 space-y-1.5">
                      {statusData.map((s) => (
                        <li key={s.name} className="flex items-center justify-between text-sm">
                          <span className="flex items-center gap-2 text-secondary-600">
                            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color }} />
                            {s.name}
                          </span>
                          <span className="font-semibold text-secondary-900">{s.value}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <EmptyState
                    icon={<HiOutlineChartBar className="w-8 h-8 text-secondary-400" />}
                    title="No call data"
                    description="Nothing to show for this period"
                  />
                )}
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
              {/* Call details */}
              <Card padding="p-4 sm:p-6">
                <h3 className="font-semibold text-secondary-900 mb-4">Call Details</h3>
                <div className="space-y-3">
                  <SummaryRow label="Connected calls" value={calls.connected} />
                  <SummaryRow label="Not connected" value={calls.notConnected} />
                  <SummaryRow label="Avg. call duration" value={formatDuration(calls.avgDuration)} />
                  <SummaryRow label="Longest call" value={formatDuration(calls.longest)} />
                  {calls.byType.map((t) => (
                    <SummaryRow key={t.type} label={`${formatLabel(t.type)} calls`} value={t.count} />
                  ))}
                </div>
              </Card>

              {/* Follow-ups */}
              <Card padding="p-4 sm:p-6">
                <h3 className="font-semibold text-secondary-900 mb-4">Follow-ups</h3>
                <div className="space-y-3">
                  <SummaryRow label="Completed" value={followUps.completed} />
                  <SummaryRow label="Pending" value={followUps.pending} />
                  <SummaryRow label="Overdue (all time)" value={followUps.overdue} />
                </div>
              </Card>

              {/* Attendance */}
              <Card padding="p-4 sm:p-6">
                <h3 className="font-semibold text-secondary-900 mb-4">Attendance</h3>
                <div className="space-y-3">
                  <SummaryRow label="Days present" value={attendance.daysPresent} />
                  <SummaryRow label="Late check-ins" value={attendance.late} />
                  <SummaryRow label="Total working time" value={formatMinutes(attendance.totalWorkingMinutes)} />
                </div>
              </Card>
            </div>

            {/* Visits & productivity */}
            {visits && productivity && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
                <Card padding="p-4 sm:p-6">
                  <h3 className="font-semibold text-secondary-900 mb-4">Visits & Meetings</h3>
                  <div className="space-y-3">
                    <SummaryRow label="Visits done / scheduled" value={`${visits.completed} / ${visits.scheduled}`} />
                    <SummaryRow label="Missed / cancelled" value={visits.noShow + visits.cancelled} />
                    <SummaryRow label="Time on visits" value={fmtMins(visits.actualMinutes)} />
                    <SummaryRow
                      label="Started on time"
                      value={visits.onTime + visits.late ? `${visits.onTimeRate}%` : '—'}
                    />
                    {visits.repeatVisits > 0 && <SummaryRow label="Repeat visits" value={visits.repeatVisits} />}
                    {Object.values(visits.outcomes || {}).map((o) => (
                      <SummaryRow key={o.label} label={o.label} value={o.count} />
                    ))}
                  </div>
                </Card>
                <Card padding="p-4 sm:p-6">
                  <h3 className="font-semibold text-secondary-900 mb-4">Productivity</h3>
                  <div className="space-y-3">
                    <SummaryRow label="Call talk time" value={fmtMins(productivity.talkMinutes)} />
                    <SummaryRow label="Time on visits" value={fmtMins(productivity.visitMinutes)} />
                    <SummaryRow label="Productive time (talk + visits)" value={fmtMins(productivity.productiveMinutes)} />
                    <SummaryRow
                      label="Working time"
                      value={productivity.attendanceMinutes ? fmtMins(productivity.attendanceMinutes) : '—'}
                    />
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm text-secondary-600">Utilisation</span>
                      <UtilisationBar value={productivity.utilisation} />
                    </div>
                  </div>
                </Card>
              </div>
            )}

            {/* Leads by stage */}
            <Card padding="p-4 sm:p-6">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                <h3 className="font-semibold text-secondary-900">My Leads by Stage</h3>
                <p className="text-sm text-secondary-500">
                  {leads.total} total · {leads.assignedInPeriod} assigned in period · {leads.conversionRate}% converted
                </p>
              </div>
              {leads.byStage.length > 0 ? (
                <div className="space-y-3">
                  {leads.byStage.map((stage) => (
                    <div key={stage.key}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-secondary-700">{stage.name}</span>
                        <span className="font-semibold text-secondary-900">{stage.count}</span>
                      </div>
                      <div className="h-2 rounded-full bg-secondary-100 overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{ width: `${(stage.count / maxStageCount) * 100}%`, backgroundColor: stage.color }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState
                  icon={<HiOutlineUsers className="w-8 h-8 text-secondary-400" />}
                  title="No leads assigned yet"
                  description="Leads assigned to you will be broken down by stage here"
                />
              )}
            </Card>
          </>
        )}
      </div>
    </UserLayout>
  );
};

export default UserReports;
